/**
 * AI Piece Selection Stats - Type Definitions
 *
 * Firebase data model for tracking per-AI-model piece performance.
 * Each AI model (Sentinel, Alchemist, Creature, etc.) gets its own
 * document in the `ai_piece_stats` collection, allowing each model
 * to develop unique piece preferences over time.
 */

import { AIModel } from '../../zodiacEngine/AI/Core/AIVariants';
import { PieceType } from '../../zodiacEngine/types';

/**
 * Per-piece performance data tracked for a single AI model.
 */
export interface PiecePerformanceStats {
  /** Number of games this piece was in the army */
  gamesInArmy: number;
  /** Number of wins when this piece was in the army */
  winsInArmy: number;
  /** Number of games this piece survived to game end */
  survivalCount: number;
  /** Number of games this piece was selected as king */
  gamesAsKing: number;
  /** Number of wins when this piece was king */
  winsAsKing: number;
  /** Per-column placement stats: wins/games when placed in column 0-4 */
  columnStats: Record<number, { games: number; wins: number }>;
}

/**
 * Pairwise synergy: tracks how well two pieces perform together.
 * Key format: "Bear+Crane" (alphabetical order, so each pair stored once).
 */
export interface SynergyStats {
  games: number;
  wins: number;
}

/**
 * Column matchup (laning) stats: tracks how a piece performs when
 * placed in the same column as a specific opponent piece.
 * Key format: "Bear>Crane" (directional — Bear's record vs Crane).
 */
export interface MatchupStats {
  games: number;
  wins: number;
}

/**
 * Root document structure for ai_piece_stats/{aiModel}
 */
export interface AIModelPieceStatsDoc {
  aiModel: string;
  totalGames: number;
  totalWins: number;
  /** Per-piece stats, keyed by PieceType string */
  pieceStats: Record<string, PiecePerformanceStats>;
  /** Pairwise synergy stats, keyed by "PieceA+PieceB" (alphabetical) */
  synergyStats: Record<string, SynergyStats>;
  /** Laning matchup stats, keyed by "OurPiece>TheirPiece" (directional) */
  matchupStats: Record<string, MatchupStats>;
  /** Timestamp of last update (epoch ms) */
  updatedAt: number;
}

/**
 * Data extracted from a completed game for stats recording.
 */
export interface GameOutcomeData {
  aiModel: AIModel;
  isLight: boolean;
  didWin: boolean;
  army: PieceType[];
  king: PieceType | undefined;
  /** Map of piece type to column index (0-4) */
  placements: Map<PieceType, number>;
  /** Pieces that survived to game end */
  survivors: PieceType[];
}

/**
 * Create an empty stats document for a new AI model.
 */
export function createEmptyStatsDoc(aiModel: AIModel): AIModelPieceStatsDoc {
  return {
    aiModel,
    totalGames: 0,
    totalWins: 0,
    pieceStats: {},
    synergyStats: {},
    matchupStats: {},
    updatedAt: Date.now(),
  };
}

/**
 * Create empty performance stats for a piece.
 */
export function createEmptyPieceStats(): PiecePerformanceStats {
  return {
    gamesInArmy: 0,
    winsInArmy: 0,
    survivalCount: 0,
    gamesAsKing: 0,
    winsAsKing: 0,
    columnStats: {},
  };
}

/**
 * Generate a canonical synergy key for two piece types.
 * Sorted alphabetically so each pair has exactly one key.
 */
export function getSynergyKey(a: PieceType, b: PieceType): string {
  return [a, b].sort().join('+');
}

/**
 * Generate a directional matchup key: our piece vs their piece.
 * NOT alphabetical — "Bear>Crane" ≠ "Crane>Bear".
 */
export function getMatchupKey(ourPiece: PieceType, theirPiece: PieceType): string {
  return `${ourPiece}>${theirPiece}`;
}

/**
 * Generate an adjacent-column matchup key: our piece vs their piece in ±1 column.
 * Uses tilde (~) to distinguish from same-column (>).
 * Stored in the same matchupStats field.
 */
export function getAdjacentMatchupKey(ourPiece: PieceType, theirPiece: PieceType): string {
  return `${ourPiece}~${theirPiece}`;
}
